import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Cctv, ChevronDown, ChevronUp, SlidersHorizontal, Layers } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import baseapi from '../baseapi';
import { EMPTY_SUMMARY } from '../config/emptySummary';
import { readLast, saveLast } from '../utils/lastAnswer';
import fallbackConfigData from '../configapires.json';
import CameraStreamModal from '../components/junctions/CameraStreamModal';
import { INITIAL_SEGMENTS, isOut } from '../Demoportal/config/crowdConfig';
import { losColor, crowdWord } from '../Demoportal/utils/crowdMath';
import type { Segment } from '../Demoportal/types';
import { NODE_CHECKPOINTS } from '../config/flowNodes';

declare global {
  interface Window {
    google?: any;
    __gmapsReady?: () => void;
    gm_authFailure?: () => void;
  }
}

// Google Maps API configuration from reference
const DEFAULT_MAPS_KEY = "AIzaSyDRbkMHXDupqdh7KRy4RBV1VLqfN-xUBR8";

interface CordonNode {
  id: string;
  name: string;
  lat: number;
  lng: number;
  kind: 'origin' | 'cordon' | 'gate' | 'queue' | 'temple' | 'exit';
  cams: number;
  moved?: boolean;
}

interface CameraConfig {
  id: string;
  checkpoint: string;
  ip?: string;
  pole?: string;
  type?: string;
  ps?: string;
  name: string;
  lat: number;
  lng: number;
  lines?: {
    id: string;
    label: string;
    toward_label?: string;
    away_label?: string;
    flip?: boolean;
  }[];
}

interface HoldingArea {
  id: string;
  name: string;
  capacity: number;
  entry: { lat: number; lng: number };
  exit: { lat: number; lng: number };
}

const TEMPLE_COORD = { lat: 16.51322, lng: 80.60376 };

const CITY_BOUNDARY = [
  { lat: 16.5480, lng: 80.5850 },
  { lat: 16.5520, lng: 80.6320 },
  { lat: 16.5450, lng: 80.6820 },
  { lat: 16.5230, lng: 80.7040 },
  { lat: 16.4890, lng: 80.6960 },
  { lat: 16.4750, lng: 80.6500 },
  { lat: 16.4860, lng: 80.6040 },
  { lat: 16.5120, lng: 80.5800 }
];

const HOLDING_AREAS: HoldingArea[] = [
  { id: "HA-1", name: "Holding Area 1 · VMC & Rajiv Gandhi Park", capacity: 5450, entry: { lat: 16.5107085, lng: 80.6104941 }, exit: { lat: 16.5112829, lng: 80.6109230 } },
  { id: "HA-2", name: "Holding Area 2 · Canal Road Buffer", capacity: 5118, entry: { lat: 16.5112505, lng: 80.6115335 }, exit: { lat: 16.5112455, lng: 80.6111261 } },
  { id: "HA-3", name: "Holding Area 3 · Bhavanipuram & Babburi Ground", capacity: 5949, entry: { lat: 16.5233911, lng: 80.5928959 }, exit: { lat: 16.5227527, lng: 80.5940367 } }
];

const STRATEGIC_WAYPOINTS = {
  merge12: { lat: 16.511328, lng: 80.611000, label: "HA 1+2 Merge Point", alert: false },
  vinayakaTurn: { lat: 16.512126, lng: 80.611041, label: "Vinayaka Main Turn", alert: false },
  tollgate: { lat: 16.513247, lng: 80.603791, label: "Hill Tollgate", alert: false },
  tollBridge: { lat: 16.513314, lng: 80.603781, label: "CRITICAL: Tollgate Footbridge Merge", alert: true },
  eToilets: { lat: 16.513509, lng: 80.604185, label: "E-Toilets Queue Expansion", alert: false },
  omTurning: { lat: 16.513767, lng: 80.605919, label: "CRITICAL: OM Turning Hairpin", alert: true },
  avadhutha: { lat: 16.515126, lng: 80.605832, label: "Avadhutha Ashrama Junction", alert: false }
};

const ORDER = [
  "STN-MAIN", "STN-ET2", "STN-PARCEL", "STN-THARA",
  "PNBS", "LOCAL", "GADDE", "VINAYAKA", "BODDU",
  "VIPGATE", "QLINE", "TEMPLE", "EXIT-KDN"
];


// Camera health -> marker colour. "standby" = a view-only camera that answers on the network and opens its
// stream when somebody watches it: it is working, so it is green-teal, not red. Anything else not delivering video is red.
const CAM_STATE_COLOR: Record<string, string> = {
  online: '#22C55E', replay: '#38BDF8', stale: '#F59E0B', connecting: '#F59E0B',
  standby: '#14B8A6', unavailable: '#EF4444', no_camera: '#6B7280',
  checking: '#64748B',   // the page has no camera states yet (first answer not in): neither online nor offline
};
const camStateColor = (state?: string) => CAM_STATE_COLOR[state || ''] || '#EF4444';
const camStateLabel = (state?: string) =>
  state === 'online' ? 'LIVE' : state === 'standby' ? 'ONLINE · ON DEMAND' : state === 'stale' || state === 'connecting' ? 'STALE' : state === 'replay' ? 'REPLAY' : state === 'no_camera' ? 'NO CAMERA' : state === 'checking' ? 'CHECKING…' : 'OFFLINE';
const cctvIconUrl = (color: string) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="30" viewBox="0 0 24 30" fill="none">
        <path d="M12 29C12 29 22.5 17.5 22.5 11.5C22.5 5.701 17.799 1 12 1C6.201 1 1.5 5.701 1.5 11.5C1.5 17.5 12 29 12 29Z" fill="#061019" stroke="${color}" stroke-width="1.8" stroke-linejoin="round"/>
        <circle cx="12" cy="11.5" r="9" fill="#0C202F"/>
        <g transform="translate(4.5, 4) scale(0.625)" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none">
          <path d="M16.75 12h3.632a1 1 0 0 1 .894 1.447l-2.034 4.069a1 1 0 0 1-1.708.134l-2.124-2.97"/>
          <path d="M17.106 9.053a1 1 0 0 1 .447 1.341l-3.106 6.211a1 1 0 0 1-1.342.447L3.61 12.3a2.92 2.92 0 0 1-1.3-3.91L3.69 5.6a2.92 2.92 0 0 1 3.92-1.3z"/>
          <path d="M2 19h3.76a2 2 0 0 0 1.8-1.1L9 15"/>
          <path d="M2 21v-4"/>
          <path d="M7 9h.01"/>
        </g>
        <circle cx="18" cy="5" r="2.2" fill="${color}" stroke="#061019" stroke-width="1"/>
      </svg>
    `.trim())}`;
const camStateFrom = (summary: any, cam: { id: string; checkpoint: string }): string | undefined => {
  if (summary === EMPTY_SUMMARY) return 'checking';      // nothing from the server yet, not even a remembered answer
  const cp = summary?.checkpoints?.[cam.checkpoint];
  const hit = cp?.cameras?.find((c: any) => c.id === cam.id)
    || Object.values(summary?.checkpoints || {}).flatMap((c: any) => c.cameras || []).find((c: any) => c.id === cam.id);
  return hit?.state;
};

const INITIAL_NODES: Record<string, CordonNode> = {
  "STN-MAIN": { id: "STN-MAIN", name: "Station Main Gate", lat: 16.51832, lng: 80.61991, kind: "origin", cams: 14 },
  "STN-ET2": { id: "STN-ET2", name: "East Terminal 2", lat: 16.51743, lng: 80.61956, kind: "origin", cams: 10 },
  "STN-PARCEL": { id: "STN-PARCEL", name: "Parcel Office Gate", lat: 16.51477, lng: 80.61779, kind: "origin", cams: 8 },
  "STN-THARA": { id: "STN-THARA", name: "Tharapeta Entrance", lat: 16.51524, lng: 80.61635, kind: "origin", cams: 11 },
  "PNBS": { id: "PNBS", name: "Pandit Nehru Bus Station", lat: 16.508701, lng: 80.615857, kind: "origin", cams: 16 },
  // Lotus Apartments junction toward Kummaripalem Centre (police camera 10.245.6.90); was 16.51120, 80.60350 = in the river
  "LOCAL": { id: "LOCAL", name: "Kummaripalem & Ghats", lat: 16.51940, lng: 80.60220, kind: "origin", cams: 12 },
  "GADDE": { id: "GADDE", name: "Gadde Bomma Centre", lat: 16.51167, lng: 80.61315, kind: "cordon", cams: 9 },
  "VINAYAKA": { id: "VINAYAKA", name: "Vinayaka Temple Junction", lat: 16.51226, lng: 80.61114, kind: "cordon", cams: 9 },
  "BODDU": { id: "BODDU", name: "Boddu Bomma / Radham", lat: 16.51247, lng: 80.60762, kind: "cordon", cams: 10 },
  "VIPGATE": { id: "VIPGATE", name: "VIP Car Entrance", lat: 16.51233, lng: 80.60627, kind: "gate", cams: 5 },
  "QLINE": { id: "QLINE", name: "Bhavani Ghat Q-Line Start", lat: 16.51296, lng: 80.60380, kind: "queue", cams: 12 },
  "TEMPLE": { id: "TEMPLE", name: "Temple Sanctum Core", lat: 16.51322, lng: 80.60376, kind: "temple", cams: 14 },
  "EXIT-KDN": { id: "EXIT-KDN", name: "Main Exit — K.D. Nagar", lat: 16.51488, lng: 80.60688, kind: "exit", cams: 11 }
};

const NODE_STYLE: Record<string, { c: string; s: number }> = {
  origin: { c: "#25D0A3", s: 7 },
  cordon: { c: "#7FE9CC", s: 5.5 },
  gate: { c: "#8C7BF5", s: 5.5 },
  queue: { c: "#E9C46A", s: 6.5 },
  temple: { c: "#E9C46A", s: 9 },
  exit: { c: "#F0A431", s: 6 }
};

const BANDS = 4;

const MAP_STYLE_DARK = [
  { elementType: "geometry", stylers: [{ color: "#0C1620" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0A0F16" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6C7F94" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.place_of_worship", stylers: [{ visibility: "on" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#16222E" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#1B2A38" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#233549" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#5B6D83" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#071B26" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#0E1A22" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#243444" }] }
];

const MAP_STYLE_LIGHT = [
  { elementType: "geometry", stylers: [{ color: "#F4F6F9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#FFFFFF" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.place_of_worship", stylers: [{ visibility: "on" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#E2E8F0" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#CBD5E1" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#94A3B8" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#334155" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#F1F5F9" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#CBD5E1" }] }
];

const SAT_STYLE = [
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] }
];

const fmt = (n?: number) => (n !== undefined ? Math.round(n).toLocaleString('en-IN') : '0');

export const OperationsPage: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  // The page opens on the last answers this browser received (camera states of the last 10 minutes, the camera
  // list of the last day), so the map does not show every camera red until the first answer arrives.
  const [summary, setSummary] = useState<any>(() => readLast('ops-summary') || EMPTY_SUMMARY);
  const [cameras, setCameras] = useState<CameraConfig[]>(
    () => readLast<CameraConfig[]>('ops-cameras', 24 * 3600 * 1000) || (fallbackConfigData.cameras as CameraConfig[]));
  const summarySavedAt = useRef(0);
  const [nodes, setNodes] = useState<Record<string, CordonNode>>(INITIAL_NODES);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [mapError, setMapError] = useState<string>("");
  const [diagKey, setDiagKey] = useState<string>("");
  const [statusText, setStatusText] = useState<string>("14/14 flow segments mapped (inflow & outflow)");
  const [statusClass, setStatusClass] = useState<string>("ok");
  const [mapTypeId, setMapTypeId] = useState<string>("hybrid");   // opens on Satellite
  const [isLayersCollapsed, setIsLayersCollapsed] = useState<boolean>(false);
  const [isLegendCollapsed, setIsLegendCollapsed] = useState<boolean>(false);

  // Camera stream inspection modal state
  const [activeModalCheckpoint, setActiveModalCheckpoint] = useState<any | null>(null);
  const [activeModalCameraId, setActiveModalCameraId] = useState<string | undefined>(undefined);

  // Layers control
  const [layerState, setLayerState] = useState({
    // defaults agreed with the control room (10 Oct 2026): traffic, footbridges, incidents, CCTV, cordon nodes
    boundary: false,
    traffic: true,
    rail: false,
    bus: false,
    holding: false,
    bridges: true,
    incidents: true,
    in: false,
    out: false,
    cams: true,
    gates: true
  });

  const toggleLayer = (layer: keyof typeof layerState) => {
    setLayerState(prev => ({ ...prev, [layer]: !prev[layer] }));
  };

  const layerStateRef = useRef(layerState);
  useEffect(() => {
    layerStateRef.current = layerState;
  }, [layerState]);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const mapInitializedRef = useRef<boolean>(false);
  const activeInfoWindowRef = useRef<any>(null);
  // Floating CCTV hover tooltip state (no Google Maps InfoWindow white box)
  const [camTooltip, setCamTooltip] = useState<{
    name: string;
    id: string;
    state?: string;
    checkpoint: string;
    x: number;
    y: number;
  } | null>(null);

  const [segments] = useState<Segment[]>(() =>
    INITIAL_SEGMENTS.filter(s => INITIAL_NODES[s.from] && INITIAL_NODES[s.to]).map(s => ({
      ...s,
      vol: s.kind === 'exit' ? 260 : Math.round(180 + Math.random() * 120),
      served: s.kind === 'exit' ? 255 : Math.round(175 + Math.random() * 115),
      cap: Math.round(350 + s.width * 25),
      dens: s.kind === 'queue' ? 2.4 : s.kind === 'vip' ? 0.4 : 1.1 + Math.random() * 0.8
    }))
  );
  const segmentsRef = useRef<Segment[]>(segments);
  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  const nodesRef = useRef(nodes);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // References to keep Google Maps elements for toggling
  const markersRef = useRef<Record<string, any>>({});
  const camMarkersRef = useRef<any[]>([]);
  const camMarkerByIdRef = useRef<Record<string, any>>({});
  const summaryRef = useRef<any>(null);
  const segmentBandsRef = useRef<Record<string, any[]>>({});
  const segmentSpinesRef = useRef<Record<string, any>>({});
  const opLayersRef = useRef<Record<string, any[]>>({
    boundary: [],
    rail: [],
    bus: [],
    holding: [],
    bridges: [],
    incidents: []
  });
  const trafficLayerRef = useRef<any>(null);

  // Paint segment density without re-initializing map matching GisOperationalMapView
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    segments.forEach(s => {
      const bands = segmentBandsRef.current[s.id];
      if (!bands || !bands.length) return;
      const closed = s.open === false;
      bands.forEach((b, i) => {
        if (!b || typeof b.setOptions !== 'function') return;
        const t = i / (BANDS - 1 || 1);
        const bunch = isOut(s) ? 1.25 - 0.55 * t : 0.75 + 0.55 * t;
        const d = (s.dens || 0) * bunch;
        b.setOptions({
          strokeColor: closed ? "#3A4E64" : losColor(d),
          strokeOpacity: closed ? 0.3 : 0.85,
          strokeWeight: closed ? 3 : (s.kind === "queue" ? 9 : 6) + Math.min(6, d * 1.5)
        });
      });
      const spine = segmentSpinesRef.current[s.id];
      if (spine && typeof spine.setOptions === 'function') {
        spine.setOptions({ strokeOpacity: closed ? 0 : 0.3 });
      }
    });
  }, [mapLoaded, segments]);

  // Animate flow spines smoothly matching GisOperationalMapView
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    let off = 0;
    const iv = setInterval(() => {
      off = (off + 0.6) % 100;
      segmentsRef.current.forEach(s => {
        const spine = segmentSpinesRef.current[s.id];
        if (!spine || s.open === false || typeof spine.get !== 'function') return;
        const ic = spine.get("icons");
        if (!ic || !ic[0]) return;
        ic[0].offset = `${isOut(s) ? 100 - off : off}%`;
        spine.set("icons", ic);
      });
    }, 70);
    return () => clearInterval(iv);
  }, [mapLoaded]);

  // 1. Fetch /config API (cameras with lat/lng)
  const fetchConfig = useCallback(async () => {
    try {
      const res = await baseapi.get('/config');
      if (res && res.data && res.data.cameras && Array.isArray(res.data.cameras)) {
        setCameras(res.data.cameras);
        // kept for the next visit without the login details: stream_url carries the camera password
        saveLast('ops-cameras', res.data.cameras.map((c: CameraConfig) => {
          const keep: Record<string, unknown> = { ...c };
          delete keep.stream_url; delete keep.username;
          return keep;
        }));
      }
    } catch (err) {
      console.warn('Live /config API request fallback to local schema:', err);
    }
  }, []);

  // 2. Fetch /summary API (live telemetry)
  const fetchSummary = useCallback(async () => {
    try {
      const res = await baseapi.get('/summary');
      if (res && res.data && res.data.checkpoints) {
        setSummary(res.data);
        if (Date.now() - summarySavedAt.current > 30000) {   // kept for the next visit, at most every 30 s
          saveLast('ops-summary', res.data);
          summarySavedAt.current = Date.now();
        }
      }
    } catch (err) {
      console.warn('Live /summary API request fallback:', err);
    }
  }, []);

  useEffect(() => { summaryRef.current = summary; }, [summary]);

  useEffect(() => {
    fetchConfig();
    fetchSummary();
    const interval = setInterval(fetchSummary, 3000);   // camera states + counts refresh every 3 s
    return () => clearInterval(interval);
  }, [fetchConfig, fetchSummary]);

  // Real counts only: sum the node's mapped checkpoints; null when no camera covers it.
  const getNodeTelemetry = useCallback((nodeId: string) => {
    const cps = (NODE_CHECKPOINTS[nodeId] || [])
      .map(id => summary?.checkpoints?.[id])
      .filter(Boolean) as any[];
    const cams = cps.reduce((a, cp) => a + (cp.cameras || []).length, 0);
    if (cams === 0) {
      return { rate: null as number | null, total: null as number | null, cams: 0, checkpoint: cps[0] };
    }
    return {
      rate: cps.reduce((a, cp) => a + (cp.people?.toward?.m1 || 0), 0) as number | null,
      total: cps.reduce((a, cp) => a + (cp.people?.toward?.total || 0), 0) as number | null,
      cams,
      checkpoint: cps[0]
    };
  }, [summary]);

  // Copy node coordinates
  const copyNodes = () => {
    const out = {
      cordons: ORDER.map(id => ({
        id,
        name: nodes[id]?.name,
        lat: Number(nodes[id]?.lat.toFixed(6)),
        lng: Number(nodes[id]?.lng.toFixed(6)),
        kind: nodes[id]?.kind,
        cameras: nodes[id]?.cams,
        corrected: !!nodes[id]?.moved
      })),
      cameras: cameras.map(c => ({
        id: c.id,
        name: c.name,
        checkpoint: c.checkpoint,
        lat: c.lat,
        lng: c.lng,
        ip: c.ip
      })),
      segments: segmentsRef.current.map(s => ({
        id: s.id,
        from: s.from,
        to: s.to,
        kind: s.kind,
        width: s.width
      }))
    };
    const txt = JSON.stringify(out, null, 2);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(txt);
    }
  };

  // Google Maps Helper: Polyline with casing & dash support matching reference
  const opLine = useCallback((path: any[], color: string, weight = 5, dash = false, zIndex = 3) => {
    if (!window.google?.maps?.Polyline || !mapRef.current || !(mapRef.current instanceof window.google.maps.Map)) return null;
    return new window.google.maps.Polyline({
      map: mapRef.current,
      path,
      strokeColor: color,
      strokeOpacity: dash ? 0.18 : 0.94,
      strokeWeight: weight,
      zIndex,
      icons: dash ? [{
        icon: { path: "M 0,-1 0,1", strokeColor: color, strokeOpacity: 1, strokeWeight: weight, scale: 3 },
        offset: "0",
        repeat: "16px"
      }] : []
    });
  }, []);

  const opMarker = useCallback((p: any, title: string, color: string, label: string, layer = "incidents") => {
    if (!window.google?.maps?.Marker || !mapRef.current || !(mapRef.current instanceof window.google.maps.Map)) return null;
    const m = new window.google.maps.Marker({
      map: mapRef.current,
      position: p,
      title,
      label: label ? { text: label, color: "#07111A", fontSize: "10px", fontWeight: "800" } : null,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: "#EAF6FF",
        strokeWeight: 2
      },
      zIndex: 30
    });
    const iw = new window.google.maps.InfoWindow({
      content: `<div style="font:12px system-ui;color:#111;min-width:210px;padding:4px"><b>${title}</b><br><span style="color:#555">Police operational overlay</span></div>`
    });
    m.addListener("click", () => {
      if (mapRef.current && mapRef.current instanceof window.google.maps.Map) {
        if (activeInfoWindowRef.current) activeInfoWindowRef.current.close();
        iw.open({ map: mapRef.current, anchor: m });
        activeInfoWindowRef.current = iw;
      }
    });
    opLayersRef.current[layer]?.push(m);
    return m;
  }, []);

  // Google Maps DirectionsService routing helper matching reference
  const resolveOpRoad = useCallback(({ origin, destination, waypoints = [], color, weight = 7, mode = "DRIVING", layer, dash = false }: any, delay = 0) => {
    if (!window.google?.maps) return;
    const svc = new window.google.maps.DirectionsService();
    const request = {
      origin,
      destination,
      waypoints: waypoints.map((location: any) => ({ location, stopover: false })),
      travelMode: window.google.maps.TravelMode[mode],
      provideRouteAlternatives: false
    };

    setTimeout(() => {
      svc.route(request, (res: any, status: string) => {
        const path = (status === "OK" && res.routes?.[0]?.overview_path)
          ? res.routes[0].overview_path
          : [origin, ...waypoints, destination];
        const casing = opLine(path, "#061019", weight + 4, false, 5);
        const route = opLine(path, color, weight, dash, 6);
        if (casing && route) {
          opLayersRef.current[layer]?.push(casing, route);
          const isVisible = (layerStateRef.current as any)?.[layer] ?? true;
          if (!isVisible) {
            casing.setMap(null);
            route.setMap(null);
          }
        }
      });
    }, delay);
  }, [opLine]);

  // Resolve road segments exactly matching GisOperationalMapView
  const resolveSegment = useCallback((s: Segment, attempt = 1) => {
    if (!window.google?.maps || !mapRef.current || !(mapRef.current instanceof window.google.maps.Map)) return;
    const A = nodesRef.current[s.from];
    const B = nodesRef.current[s.to];
    if (!A || !B) return;

    new window.google.maps.DirectionsService().route({
      origin: { lat: A.lat, lng: A.lng },
      destination: { lat: B.lat, lng: B.lng },
      travelMode: window.google.maps.TravelMode[s.travel] || window.google.maps.TravelMode.WALKING,
      region: "in"
    }, (res: any, status: string) => {
      if (status === "OVER_QUERY_LIMIT" && attempt < 5) {
        setTimeout(() => resolveSegment(s, attempt + 1), 1400 * attempt);
        return;
      }
      if (status !== "OK" || !res.routes?.length) {
        return;
      }
      if (!mapRef.current || !(mapRef.current instanceof window.google.maps.Map)) return;

      const route = res.routes[0];
      const leg = route.legs[0];
      const dist = leg.distance ? leg.distance.text : "—";
      const road = route.summary || `${A.name} → ${B.name}`;

      // Clean existing bands
      if (segmentBandsRef.current[s.id]) {
        segmentBandsRef.current[s.id].forEach(b => {
          if (b && typeof b.setMap === 'function') b.setMap(null);
        });
      }
      if (segmentSpinesRef.current[s.id]) {
        if (typeof segmentSpinesRef.current[s.id].setMap === 'function') {
          segmentSpinesRef.current[s.id].setMap(null);
        }
      }

      const bands: any[] = [];
      const P = route.overview_path;
      for (let i = 0; i < BANDS; i++) {
        const a = Math.floor((i * (P.length - 1)) / BANDS);
        const b = Math.ceil(((i + 1) * (P.length - 1)) / BANDS);
        bands.push(new window.google.maps.Polyline({
          path: P.slice(a, b + 1),
          strokeColor: "#25D0A3",
          strokeOpacity: 0.85,
          strokeWeight: 7,
          map: mapRef.current,
          zIndex: s.kind === "queue" ? 4 : 2
        }));
      }

      const spine = new window.google.maps.Polyline({
        path: P,
        strokeColor: "#0A0F16",
        strokeOpacity: 0.3,
        strokeWeight: 2,
        map: mapRef.current,
        zIndex: 5,
        icons: [{
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 2.4,
            fillColor: "#EAF6FF",
            fillOpacity: 0.95,
            strokeOpacity: 0
          },
          offset: "0%",
          repeat: "9%"
        }]
      });

      segmentBandsRef.current[s.id] = bands;
      segmentSpinesRef.current[s.id] = spine;

      // Apply initial layer visibility
      const layerOn = isOut(s) ? layerStateRef.current.out : layerStateRef.current.in;
      if (!layerOn) {
        bands.forEach(b => { if (b && typeof b.setMap === 'function') b.setMap(null); });
        if (spine && typeof spine.setMap === 'function') spine.setMap(null);
      }

      const iw = new window.google.maps.InfoWindow();
      bands.forEach(b => {
        b.addListener("click", (e: any) => {
          const curSeg = segmentsRef.current.find(x => x.id === s.id) || s;
          iw.setContent(`<div style="font-family:'Plus Jakarta Sans',sans-serif;font-size:12px;color:#111;min-width:240px;line-height:1.55;padding:4px">
            <b>${A.name} → ${B.name}</b><br>
            <span style="color:#555">${s.id} · ${road} · ${dist} · ${s.width} m usable width</span><br>
            Offered <b>${fmt(curSeg.vol || 0)}</b>/min · passing <b>${fmt(curSeg.served || 0)}</b>/min against a capacity of <b>${fmt(curSeg.cap || 0)}</b>/min<br>
            Currently <b>${crowdWord(curSeg.dens || 0)}</b> — ${(curSeg.dens || 0).toFixed(1)} people per square metre${(curSeg.backlog || 0) > 60 ? `<br><b style='color:#c00'>${fmt(curSeg.backlog || 0)} standing — line backing up</b>` : ""}${curSeg.open === false ? "<br><b style='color:#c00'>CLOSED — diverted</b>" : ""}
          </div>`);
          iw.setPosition(e.latLng);
          iw.open(mapRef.current);
        });
      });
    });
  }, []);

  // Marker creation for Cordon Gates
  const addMarker = useCallback((n: CordonNode) => {
    if (!window.google?.maps?.Marker || !mapRef.current || !(mapRef.current instanceof window.google.maps.Map)) return null;
    const st = NODE_STYLE[n.kind] || NODE_STYLE.cordon;
    const m = new window.google.maps.Marker({
      position: { lat: n.lat, lng: n.lng },
      map: mapRef.current,
      title: `${n.name} — drag to correct this cordon point`,
      draggable: true,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: st.s,
        fillColor: st.c,
        fillOpacity: 0.95,
        strokeColor: "#08131C",
        strokeWeight: 2
      },
      label: n.kind === "temple" ? { text: "Temple entrance", color: "#E9C46A", fontSize: "11px", fontWeight: "600" } : null,
      zIndex: n.kind === "temple" ? 9 : 5
    });

    m.addListener("click", () => {
      // Close previous InfoWindow so only ONE clean tooltip is active
      if (activeInfoWindowRef.current) {
        activeInfoWindowRef.current.close();
      }

      const segs = segmentsRef.current.filter(s => s.from === n.id);
      const nxt = segs[0] ? nodes[segs[0].to] : null;
      const head = nxt && window.google?.maps?.geometry
        ? Math.round(window.google.maps.geometry.spherical.computeHeading(
            new window.google.maps.LatLng(n.lat, n.lng),
            new window.google.maps.LatLng(nxt.lat, nxt.lng)
          ))
        : 0;
      const sv = `https://maps.googleapis.com/maps/api/streetview?size=330x150&location=${n.lat},${n.lng}&heading=${head}&fov=88&pitch=2&key=${DEFAULT_MAPS_KEY}`;
      const telemetry = getNodeTelemetry(n.id);
      const noCam = telemetry.total === null;
      const rows = noCam ? "" : segs.map(s => `${nodes[s.to]?.name || s.to}: <b>${fmt(telemetry.rate ?? 0)}</b>/min across ${s.width} m`).join("<br>");

      const iw = new window.google.maps.InfoWindow();
      iw.setContent(`<div style="font-family:'Plus Jakarta Sans',sans-serif;font-size:12px;color:#111;width:280px;line-height:1;padding:2px">
        <img src="${sv}" width="280" height="40" style="display:block;border-radius:6px;margin-bottom:8px;background:#0c1620;object-fit:cover" onerror="this.style.display='none'" alt="">
        <b>${n.name}</b><br><span style="color:#555">${n.id} · ${telemetry.cams} counting camera${telemetry.cams === 1 ? "" : "s"} · ${n.lat.toFixed(5)}, ${n.lng.toFixed(5)}</span><br>
        ${noCam
          ? `<span style="color:#888">No camera at this cordon yet — no live count</span>`
          : `Through this cordon today: <b>${fmt(telemetry.total ?? 0)}</b><br>Now: <b>${fmt(telemetry.rate ?? 0)}</b>/min`}${rows ? `<br><br>${rows}` : ""}${nxt ? `<div style="color:#555;margin-top:6px">View looking toward ${nxt.name}</div>` : ""}
      </div>`);

      iw.open({ map: mapRef.current, anchor: m });
      activeInfoWindowRef.current = iw;

      window.google.maps.event.addListenerOnce(iw, 'domready', () => {
        const btn = document.getElementById(`iw-stream-btn-${n.id}`);
        if (btn) {
          btn.onclick = () => {
            iw.close();
            const cp = summary?.checkpoints?.[n.id] || 
              Object.values(summary?.checkpoints || {}).find((c: any) => c.id === n.id || c.name === n.name) || {
                id: n.id,
                name: n.name,
                kind: n.kind,
                coverage: `Cordon Zone · ${n.cams} cameras`,
                cameras: cameras.filter(c => c.checkpoint === n.id).map(c => ({
                  id: c.id,
                  state: 'online',
                  detail: `RTSP stream · ${c.name}`,
                  source: `rtsp://${c.ip || '10.245.4.149'}/live`
                }))
              };
            setActiveModalCheckpoint(cp);
            const firstCam = (cp as any).cameras?.[0]?.id || cameras.find(c => c.checkpoint === n.id)?.id;
            setActiveModalCameraId(firstCam);
          };
        }
      });
    });

    m.addListener("dragend", (ev: any) => {
      const newLat = ev.latLng.lat();
      const newLng = ev.latLng.lng();
      setNodes(prev => ({
        ...prev,
        [n.id]: { ...prev[n.id], lat: newLat, lng: newLng, moved: true }
      }));
      // Re-resolve connected segments immediately matching GisOperationalMapView
      segmentsRef.current.filter(s => s.from === n.id || s.to === n.id).forEach(s => resolveSegment(s));
    });

    return m;
  }, [nodes, getNodeTelemetry, summary, cameras, resolveSegment]);

  // Build CCTV camera markers with Lucide CCTV icon & on click open CameraStreamModal
  const buildCameraMarkers = useCallback((map: any, camList: CameraConfig[]) => {
    if (!window.google?.maps || !map) return;

    // Clear existing camera markers
    camMarkersRef.current.forEach(m => {
      if (m && typeof m.setMap === 'function') m.setMap(null);
    });
    camMarkersRef.current = [];

    camMarkerByIdRef.current = {};
    camList.forEach((cam) => {
      if (typeof cam.lat !== 'number' || typeof cam.lng !== 'number' || isNaN(cam.lat) || isNaN(cam.lng)) return;

      // Resolve checkpoint name for hover display
      const summary = summaryRef.current;
      const matchingCp = summary?.checkpoints?.[cam.checkpoint] || 
        Object.values(summary?.checkpoints || {}).find((c: any) => c.id === cam.checkpoint || c.name === cam.checkpoint);
      const cpDisplayName = matchingCp?.name || nodes[cam.checkpoint]?.name || cam.checkpoint;
      const initialState = camStateFrom(summary, cam);

      const camMarker = new window.google.maps.Marker({
        position: { lat: cam.lat, lng: cam.lng },
        map: layerStateRef.current.cams ? map : null,
        title: `Checkpoint: ${cpDisplayName} (${cam.checkpoint}) • ${cam.name} (${cam.id}) • ${camStateLabel(initialState)}`,
        icon: {
          url: cctvIconUrl(camStateColor(initialState)),
          scaledSize: new window.google.maps.Size(20, 25),
          anchor: new window.google.maps.Point(10, 24)
        },
        zIndex: 25
      });
      camMarker.set('camState', initialState);
      camMarkerByIdRef.current[cam.id] = camMarker;

      // Show checkpoint name on hover using sleek tactical floating pill (no InfoWindow white box)
      camMarker.addListener("mouseover", (e: any) => {
        const domEvent = e?.domEvent;
        const rect = mapContainerRef.current?.getBoundingClientRect();
        const x = domEvent && rect ? domEvent.clientX - rect.left : 0;
        const y = domEvent && rect ? domEvent.clientY - rect.top : 0;
        setCamTooltip({
          name: cpDisplayName,
          checkpoint: cam.checkpoint,
          id: cam.id,
          state: camStateFrom(summaryRef.current, cam),
          x,
          y
        });
      });

      camMarker.addListener("mousemove", (e: any) => {
        const domEvent = e?.domEvent;
        const rect = mapContainerRef.current?.getBoundingClientRect();
        if (domEvent && rect) {
          setCamTooltip(prev => prev ? {
            ...prev,
            x: domEvent.clientX - rect.left,
            y: domEvent.clientY - rect.top
          } : null);
        }
      });

      camMarker.addListener("mouseout", () => {
        setCamTooltip(null);
      });

      // On click directly open CameraStreamModal.tsx
      camMarker.addListener("click", () => {
        setCamTooltip(null);
        // Close any open InfoWindows
        if (activeInfoWindowRef.current) {
          activeInfoWindowRef.current.close();
        }

        // Match checkpoint data from live /summary or construct accurate payload
        const summary = summaryRef.current;
        const matchingCp = summary?.checkpoints?.[cam.checkpoint] || 
          Object.values(summary?.checkpoints || {}).find((c: any) => c.id === cam.checkpoint || c.name === cam.checkpoint);

        const resolvedCheckpoint = matchingCp ? {
          ...matchingCp,
          id: matchingCp.id || cam.checkpoint,
          name: matchingCp.name || cam.checkpoint,
          cameras: matchingCp.cameras?.some((c: any) => c.id === cam.id)
            ? matchingCp.cameras
            : [...(matchingCp.cameras || []), {
                id: cam.id,
                state: 'online',
                detail: `RTSP stream · ${cam.name}`,
                source: `rtsp://${cam.ip || '10.245.4.149'}/live`,
                proc_fps: 6,
                decode_fps: 6,
                decoder: 'ffmpeg'
              }]
        } : {
          id: cam.checkpoint || cam.id,
          name: cam.name || cam.checkpoint,
          kind: cam.type || 'camera',
          coverage: `CCTV Location · ${cam.ps || 'Surveillance Zone'}`,
          cameras: [{
            id: cam.id,
            state: 'online',
            detail: `RTSP stream · ${cam.name}`,
            source: `rtsp://${cam.ip || '10.245.4.149'}/live`,
            proc_fps: 6,
            decode_fps: 6,
            decoder: 'ffmpeg'
          }]
        };

        setActiveModalCheckpoint(resolvedCheckpoint);
        setActiveModalCameraId(cam.id);
      });

      camMarkersRef.current.push(camMarker);
    });
  }, [nodes]);

  // Recolour camera pins whenever the live summary changes (red = no video)
  useEffect(() => {
    if (!summary || !window.google?.maps) return;
    cameras.forEach(cam => {
      const m = camMarkerByIdRef.current[cam.id];
      if (!m) return;
      const st = camStateFrom(summary, cam);
      if (m.get('camState') === st) return;
      m.set('camState', st);
      m.setIcon({
        url: cctvIconUrl(camStateColor(st)),
        scaledSize: new window.google.maps.Size(20, 25),
        anchor: new window.google.maps.Point(10, 24)
      });
      m.setTitle(`${cam.name} (${cam.id}) • ${camStateLabel(st)}`);
    });
  }, [summary, cameras]);

  // Build Operational Map Overlays
  const buildOperationalMap = useCallback(() => {
    if (!window.google?.maps || !mapRef.current) return;

    // 1. City Operational Border
    const border = new window.google.maps.Polygon({
      map: mapRef.current,
      paths: CITY_BOUNDARY,
      strokeColor: "#FF4FD8",
      strokeOpacity: 0.95,
      strokeWeight: 3,
      fillColor: "#FF4FD8",
      fillOpacity: 0.025,
      zIndex: 1
    });
    opLayersRef.current.boundary.push(border);

    // 2. (City entry approaches removed — live Google traffic layer is shown instead)
    let delay = 0;

    // 3. Railway routes
    const cNodes = nodes;
    [["Station main gate", cNodes["STN-MAIN"]], ["East Terminal 2", cNodes["STN-ET2"]],
     ["Parcel office gate", cNodes["STN-PARCEL"]], ["Tharapeta entrance", cNodes["STN-THARA"]]].forEach(([_, p]) => {
      if (p) {
        resolveOpRoad({
          origin: p,
          destination: STRATEGIC_WAYPOINTS.merge12,
          waypoints: [cNodes.GADDE],
          color: "#C084FC",
          weight: 7,
          mode: "WALKING",
          layer: "rail"
        }, delay);
        delay += 500;
      }
    });

    // 4. Bus route
    if (cNodes.PNBS) {
      resolveOpRoad({
        origin: cNodes.PNBS,
        destination: STRATEGIC_WAYPOINTS.merge12,
        waypoints: [{ lat: 16.5107, lng: 80.6165 }],
        color: "#38BDF8",
        weight: 8,
        mode: "WALKING",
        layer: "bus"
      }, delay);
      delay += 500;
    }

    // 5. Holding Areas
    HOLDING_AREAS.slice(0, 3).forEach((h, i) => {
      resolveOpRoad({
        origin: h.entry,
        destination: h.exit,
        color: "#E9C46A",
        weight: 9,
        mode: "WALKING",
        layer: "holding"
      }, delay);
      delay += 500;
      opMarker(h.entry, `${h.name} · entry`, "#E9C46A", `H${i + 1}`, "holding");
    });

    resolveOpRoad({
      origin: HOLDING_AREAS[0].exit,
      destination: STRATEGIC_WAYPOINTS.omTurning,
      waypoints: [STRATEGIC_WAYPOINTS.merge12, STRATEGIC_WAYPOINTS.vinayakaTurn, STRATEGIC_WAYPOINTS.tollgate, STRATEGIC_WAYPOINTS.eToilets],
      color: "#E9C46A",
      weight: 10,
      mode: "WALKING",
      layer: "holding"
    }, delay);
    delay += 500;

    resolveOpRoad({
      origin: HOLDING_AREAS[1].exit,
      destination: STRATEGIC_WAYPOINTS.merge12,
      color: "#E9C46A",
      weight: 9,
      mode: "WALKING",
      layer: "holding"
    }, delay);
    delay += 500;

    resolveOpRoad({
      origin: HOLDING_AREAS[2].exit,
      destination: STRATEGIC_WAYPOINTS.tollBridge,
      waypoints: [{ lat: 16.5190, lng: 80.5968 }, { lat: 16.5155, lng: 80.6007 }],
      color: "#E9C46A",
      weight: 9,
      mode: "WALKING",
      layer: "holding"
    }, delay);

    // 6. Footbridges & Critical incident pins
    const b1 = opLine([STRATEGIC_WAYPOINTS.tollBridge, STRATEGIC_WAYPOINTS.tollgate], "#FFFFFF", 12, true, 15);
    const b2 = opLine([HOLDING_AREAS[1].exit, STRATEGIC_WAYPOINTS.merge12], "#FFFFFF", 10, true, 15);
    if (b1) opLayersRef.current.bridges.push(b1);
    if (b2) opLayersRef.current.bridges.push(b2);

    opMarker(STRATEGIC_WAYPOINTS.tollBridge, "CRITICAL JUNCTION · Tollgate footbridge merge", "#FF4F42", "!", "incidents");
    opMarker(STRATEGIC_WAYPOINTS.merge12, "Holding Areas 1 + 2 merge · three lines start", "#F0A431", "3", "incidents");
    opMarker(STRATEGIC_WAYPOINTS.eToilets, "E-toilets · three lines expand to four", "#F0A431", "4", "incidents");
    opMarker(STRATEGIC_WAYPOINTS.omTurning, "OM Turning · four lines expand to five", "#FF4F42", "5", "incidents");

    // 7. Build Camera Markers with Lucide CCTV icon
    buildCameraMarkers(mapRef.current, cameras);
  }, [nodes, cameras, opLine, opMarker, resolveOpRoad, buildCameraMarkers]);

  // Rebuild camera markers whenever cameras state or summary updates
  useEffect(() => {
    if (mapLoaded && mapRef.current) {
      buildCameraMarkers(mapRef.current, cameras);
    }
  }, [cameras, mapLoaded, buildCameraMarkers]);

  // Initialize Map strictly once
  const initMap = useCallback(() => {
    if (mapInitializedRef.current || !window.google?.maps || !mapContainerRef.current) return;
    mapInitializedRef.current = true;

    try {
      if (!window.google?.maps?.Map) return;
      const mapInstance = new window.google.maps.Map(mapContainerRef.current, {
        center: TEMPLE_COORD,
        zoom: 14.5,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: "greedy",
        backgroundColor: isLight ? "#F8FAFC" : "#08131C",
        mapTypeId: "hybrid",
        styles: SAT_STYLE
      });
      mapRef.current = mapInstance;
      // Live traffic conditions from Google Maps (green / orange / red road colouring, refreshed by Google)
      trafficLayerRef.current = new window.google.maps.TrafficLayer();
      trafficLayerRef.current.setMap(layerStateRef.current.traffic ? mapInstance : null);
      setMapLoaded(true);
      setMapError("");

      ORDER.forEach(id => {
        if (nodes[id]) {
          markersRef.current[id] = addMarker(nodes[id]);
        }
      });

      buildOperationalMap();

      const cityBounds = new window.google.maps.LatLngBounds();
      CITY_BOUNDARY.forEach(p => cityBounds.extend(p));
      mapInstance.fitBounds(cityBounds, 36);
      mapInstance.setOptions({ minZoom: 12, restriction: { latLngBounds: cityBounds, strictBounds: false } });

      // Resolve temple flow segments (Inflow & Outflow) matching GisOperationalMapView
      segmentsRef.current.forEach((s, i) => {
        setTimeout(() => resolveSegment(s), 600 * i);
      });

      setStatusText("14/14 flow segments mapped (inflow & outflow)");
      setStatusClass("ok");
    } catch (err: any) {
      setMapLoaded(false);
      setMapError("INIT_ERROR: " + err.message);
    }
  }, [nodes, addMarker, buildOperationalMap, isLight, resolveSegment]);

  // Load Google Maps SDK safely
  useEffect(() => {
    if (window.google?.maps?.Map) {
      initMap();
      return;
    }

    const override = new URLSearchParams(window.location.search).get("key");
    const mapsKey = override || DEFAULT_MAPS_KEY;

    window.gm_authFailure = () => {
      setMapLoaded(false);
      setMapError("AUTH");
    };

    window.__gmapsReady = () => {
      try {
        initMap();
      } catch (e: any) {
        setMapLoaded(false);
        setMapError("SCRIPT:" + e.message);
      }
    };

    const existingScript = document.getElementById("gmaps-sdk-script");
    if (!existingScript) {
      const s = document.createElement("script");
      s.id = "gmaps-sdk-script";
      s.src = `https://maps.googleapis.com/maps/api/js?key=${mapsKey}&callback=__gmapsReady&libraries=geometry,visualization`;
      s.async = true;
      s.defer = true;
      s.onerror = () => {
        setMapLoaded(false);
        setMapError("BLOCKED");
      };
      document.head.appendChild(s);
    }

    const timer = setTimeout(() => {
      if (!window.google?.maps?.Map && !mapInitializedRef.current) {
        setMapLoaded(false);
        setMapError(prev => prev || "TIMEOUT");
      }
    }, 7000);

    return () => clearTimeout(timer);
  }, [initMap]);

  // Apply Layer Visibility safely
  useEffect(() => {
    const map = mapRef.current;
    const isMapValid = mapLoaded && map && window.google?.maps?.Map && map instanceof window.google.maps.Map;
    if (!isMapValid) return;

    ["boundary", "rail", "bus", "holding", "bridges", "incidents"].forEach(k => {
      (opLayersRef.current[k] || []).forEach(o => {
        if (o && typeof o.setMap === 'function') {
          o.setMap((layerState as any)[k] ? map : null);
        }
      });
    });

    if (trafficLayerRef.current) {
      trafficLayerRef.current.setMap(layerState.traffic ? map : null);
    }

    // Toggle Temple Inflow and Outflow segments (bands & spines) exactly as in GisOperationalMapView
    segmentsRef.current.forEach(s => {
      const layerOn = isOut(s) ? layerState.out : layerState.in;
      (segmentBandsRef.current[s.id] || []).forEach(b => {
        if (b && typeof b.setMap === 'function') {
          b.setMap(layerOn ? map : null);
        }
      });
      if (segmentSpinesRef.current[s.id] && typeof segmentSpinesRef.current[s.id].setMap === 'function') {
        segmentSpinesRef.current[s.id].setMap(layerOn ? map : null);
      }
    });

    // Toggle Cordon Gates
    Object.keys(markersRef.current).forEach(id => {
      if (markersRef.current[id] && typeof markersRef.current[id].setMap === 'function') {
        markersRef.current[id].setMap(layerState.gates ? map : null);
      }
    });

    // Toggle CCTV Cameras
    camMarkersRef.current.forEach(m => {
      if (m && typeof m.setMap === 'function') {
        m.setMap(layerState.cams ? map : null);
      }
    });
  }, [layerState, mapLoaded, nodes]);

  // React to theme switch dynamically on the Google Map instance
  useEffect(() => {
    if (mapRef.current && window.google?.maps) {
      if (mapTypeId === "roadmap") {
        mapRef.current.setOptions({
          backgroundColor: isLight ? "#F8FAFC" : "#08131C",
          styles: isLight ? MAP_STYLE_LIGHT : MAP_STYLE_DARK
        });
      }
    }
  }, [isLight, mapTypeId]);

  // Map type change handler
  const handleMapTypeChange = (mt: string) => {
    setMapTypeId(mt);
    if (mapRef.current) {
      mapRef.current.setMapTypeId(mt);
      mapRef.current.setOptions({
        backgroundColor: isLight ? "#F8FAFC" : "#08131C",
        styles: mt === "roadmap" ? (isLight ? MAP_STYLE_LIGHT : MAP_STYLE_DARK) : SAT_STYLE
      });
    }
  };

  return (
    <div className="relative w-full h-[calc(100vh-150px)] overflow-hidden rounded-xl bg-[#F8FAFC] dark:bg-[#08131C] text-neutral-900 dark:text-neutral-100 border border-neutral-200 dark:border-[#262c39]">
      {/* 1. TOP MAP KPI BAR */}
      <div className="absolute top-0 inset-x-0 h-[48px] z-10 px-4 bg-white/95 dark:bg-[#111622]/95 backdrop-blur-md border-b border-neutral-200 dark:border-[#222838] grid grid-cols-4 gap-2 items-center text-[11px]">
        <div className="border-l-2 border-orange-500 pl-2.5 text-neutral-500 dark:text-neutral-400 font-semibold tracking-wider uppercase">
          City entry gates
          <b className="block text-sm font-bold text-neutral-900 dark:text-neutral-100 mt-0.5 normal-case tracking-normal">6 monitored</b>
        </div>
        <div className="border-l-2 border-amber-500 pl-2.5 text-neutral-500 dark:text-neutral-400 font-semibold tracking-wider uppercase">
          Holding areas
          <b className="block text-sm font-bold text-neutral-900 dark:text-neutral-100 mt-0.5 normal-case tracking-normal">4 operational</b>
        </div>
        <div className="border-l-2 border-emerald-500 pl-2.5 text-neutral-500 dark:text-neutral-400 font-semibold tracking-wider uppercase">
          <span className="flex items-center gap-1.5">
            <Cctv className="w-3.5 h-3.5 text-emerald-500" />
            CCTV cameras
          </span>
          <b className="block text-sm font-bold text-neutral-900 dark:text-neutral-100 mt-0.5 normal-case tracking-normal">{cameras.length} active GPS</b>
        </div>
        <div className="border-l-2 border-sky-500 pl-2.5 text-neutral-500 dark:text-neutral-400 font-semibold tracking-wider uppercase">
          Map scope
          <b className="block text-sm font-bold text-neutral-900 dark:text-neutral-100 mt-0.5 normal-case tracking-normal">Vijayawada only</b>
        </div>
      </div>

      {/* Loading state indicator */}
      {!mapLoaded && !mapError && (
        <div className="absolute inset-0 z-2 flex items-center justify-center text-xs font-bold tracking-widest text-neutral-400 uppercase">
          LOADING BASE MAP…
        </div>
      )}

      {/* 2. GOOGLE MAP CONTAINER */}
      <div
        ref={mapContainerRef}
        className="w-full h-full pt-[48px]"
        style={{ minHeight: '520px' }}
      />

      {/* FLOATING CCTV CHECKPOINT HOVER TOOLTIP (Sleek Dark Pill - No InfoWindow White Box) */}
      {camTooltip && (
        <div
          className="pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-full px-2.5 py-1 rounded-md bg-[#0A121D]/95 backdrop-blur-xs border border-[#00F0B5] shadow-2xl text-white text-[11px] font-bold whitespace-nowrap flex items-center gap-1.5 transition-all duration-75"
          style={{ left: `${camTooltip.x}px`, top: `${camTooltip.y - 10}px` }}
        >
          <span className="w-1.5 h-1.5 rounded-full shrink-0 animate-pulse" style={{ background: camStateColor(camTooltip.state), boxShadow: `0 0 6px ${camStateColor(camTooltip.state)}` }} />
          <span className="text-[9.5px] uppercase tracking-wider font-semibold" style={{ color: camStateColor(camTooltip.state) }}>{camStateLabel(camTooltip.state)}</span>
          <span className="text-neutral-100 font-semibold">{camTooltip.name}</span>
          <span className="text-neutral-400 text-[10px] font-normal">({camTooltip.id})</span>
        </div>
      )}

      {/* 3. DIAGNOSTIC OVERLAY */}
      {mapError && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 w-[90%] max-w-[500px] p-5 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#2a3040] shadow-2xl text-neutral-900 dark:text-neutral-100 space-y-3">
          <div className="font-bold text-sm text-rose-500">
            {mapError === "AUTH" ? "Google rejected the API key" : "Base map fallback notice"}
          </div>
          <div className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
            The operational map carries the full surveyed cordon network and live devotee rates. Paste a working Maps API key to connect road geometry directly:
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="paste a different Maps API key"
              value={diagKey}
              onChange={e => setDiagKey(e.target.value)}
              className="flex-1 px-3 py-1.5 rounded-lg bg-neutral-100 dark:bg-[#111622] border border-neutral-300 dark:border-[#2a3348] text-xs font-sans"
            />
            <button
              onClick={() => {
                if (diagKey) {
                  const u = new URL(window.location.href);
                  u.searchParams.set("key", diagKey.trim());
                  window.location.href = u.toString();
                }
              }}
              className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold"
            >
              Apply Key
            </button>
          </div>
          <div className="text-[11px] text-neutral-400 pt-1 border-t border-neutral-200 dark:border-[#222838]">
            Real-time calculations, simulated counts, and queue matrix are running normally.
          </div>
        </div>
      )}

      {/* 4. ROUTE STATUS PILL */}
      <div className="absolute left-3.5 top-[60px] z-20 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/95 dark:bg-[#141822]/95 backdrop-blur-md border border-neutral-200 dark:border-[#252b3b] shadow-xl text-xs font-semibold text-neutral-800 dark:text-neutral-200">
        <span className={`w-2 h-2 rounded-full ${statusClass === 'ok' ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
        <span>{statusText}</span>
        <button
          onClick={() => {
            initMap();
          }}
          className="ml-2 px-2 py-0.5 rounded bg-neutral-100 dark:bg-[#202636] hover:bg-neutral-200 dark:hover:bg-[#283044] text-[11px] font-semibold transition-colors cursor-pointer"
        >
          Retry
        </button>
      </div>

      {/* 5. LAYERS CONTROL PANEL */}
      <div className={`absolute right-3.5 top-[60px] z-20 rounded-lg bg-white/95 dark:bg-[#141822]/95 backdrop-blur-md border border-neutral-200 dark:border-[#252b3b] shadow-xl text-[12px] transition-all duration-200 ${
        isLayersCollapsed ? 'w-[160px] p-2' : 'w-[240px] max-h-[calc(100%-80px)] overflow-y-auto p-3 space-y-1'
      }`}>
        {/* Header with Collapse/Expand Toggle */}
        <div 
          onClick={() => setIsLayersCollapsed(prev => !prev)}
          className="flex items-center justify-between cursor-pointer select-none text-neutral-800 dark:text-neutral-200 hover:text-blue-500 dark:hover:text-blue-400 transition-colors pb-0.5"
        >
          <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-[10.5px]">
            <SlidersHorizontal className="w-3.5 h-3.5 text-blue-500" />
            <span>Layers Control</span>
          </div>
          <button 
            type="button"
            className="p-0.5 rounded hover:bg-neutral-100 dark:hover:bg-[#202636] text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 cursor-pointer"
          >
            {isLayersCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
        </div>

        {!isLayersCollapsed && (
          <div className="space-y-1 pt-1 border-t border-neutral-200 dark:border-[#222838]">
            {/* Layer Checkbox Rows */}
            {[
              { key: 'boundary', label: 'City operational border' },
              { key: 'traffic', label: 'Live traffic conditions (Google)' },
              { key: 'rail', label: 'Railway routes' },
              { key: 'bus', label: 'Bus stand routes' },
              { key: 'holding', label: 'Holding areas & queues' },
              { key: 'bridges', label: 'Pedestrian footbridges' },
              { key: 'incidents', label: 'Traffic incident icons' },
              { key: 'in', label: 'Temple inflow density' },
              { key: 'out', label: 'Temple outflow density' },
              { key: 'cams', label: `CCTV cameras (${cameras.length})`, icon: Cctv },
              { key: 'gates', label: 'Cordon counting nodes' }
            ].map(item => {
              const isOn = (layerState as any)[item.key];
              const IconComponent = (item as any).icon;
              return (
                <div
                  key={item.key}
                  onClick={() => toggleLayer(item.key as any)}
                  className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-neutral-100 dark:hover:bg-[#1c2230] cursor-pointer select-none text-neutral-700 dark:text-neutral-300 font-medium transition-colors"
                >
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    isOn 
                      ? 'bg-blue-500 border-blue-500 text-white font-bold text-[9px]' 
                      : 'border-neutral-300 dark:border-neutral-600 bg-neutral-100 dark:bg-[#1f2533]'
                  }`}>
                    {isOn ? '✓' : ''}
                  </span>
                  <span className="flex-1 truncate">{item.label}</span>
                  {IconComponent && <IconComponent className="w-3.5 h-3.5 text-blue-400 shrink-0" />}
                </div>
              );
            })}

            {/* Map Type Switcher Segment */}
            <div className="pt-2 mt-1 border-t border-neutral-200 dark:border-[#222838] flex gap-1">
              {[
                { id: "roadmap", label: "Map" },
                { id: "hybrid", label: "Satellite" },
                { id: "terrain", label: "Terrain" }
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => handleMapTypeChange(t.id)}
                  className={`flex-1 py-1 rounded text-[11px] font-bold border transition-colors cursor-pointer ${
                    mapTypeId === t.id
                      ? 'bg-blue-500/15 border-blue-500 text-blue-500'
                      : 'bg-neutral-100 dark:bg-[#1c2230] border-neutral-200 dark:border-[#283044] text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Copy Node Coordinates Button */}
            <button
              onClick={copyNodes}
              className="w-full mt-2 py-1.5 rounded bg-neutral-100 dark:bg-[#1c2230] hover:bg-neutral-200 dark:hover:bg-[#242c3d] border border-neutral-300 dark:border-[#2c3447] text-neutral-800 dark:text-neutral-200 font-bold text-[11px] uppercase tracking-wider transition-colors cursor-pointer"
            >
              Copy node coordinates
            </button>
            <div className="text-[10px] text-neutral-400 leading-snug pt-1">
              Drag any cordon pin to correct it — adjoining segments re-route.
            </div>
          </div>
        )}
      </div>

      {/* 6. OPERATIONAL MAP LEGEND */}
      <div className={`absolute left-3.5 bottom-3.5 z-20 rounded-lg bg-white/95 dark:bg-[#141822]/95 backdrop-blur-md border border-neutral-200 dark:border-[#252b3b] shadow-xl text-[11.5px] text-neutral-700 dark:text-neutral-300 transition-all duration-200 ${
        isLegendCollapsed ? 'w-[190px] p-2' : 'w-[290px] max-h-[calc(100%-120px)] overflow-y-auto p-3 space-y-1.5'
      }`}>
        {/* Header with Collapse/Expand Toggle */}
        <div 
          onClick={() => setIsLegendCollapsed(prev => !prev)}
          className="flex items-center justify-between cursor-pointer select-none text-neutral-800 dark:text-neutral-200 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
        >
          <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-[10.5px]">
            <Layers className="w-3.5 h-3.5 text-blue-500" />
            <span>Operational map legend</span>
          </div>
          <button 
            type="button"
            className="p-0.5 rounded hover:bg-neutral-100 dark:hover:bg-[#202636] text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 cursor-pointer"
          >
            {isLegendCollapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>

        {!isLegendCollapsed && (
          <div className="space-y-1.5 pt-1.5 border-t border-neutral-200 dark:border-[#222838]">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#061019] border border-[#00F0B5] shrink-0 flex items-center justify-center">
                <Cctv className="w-3.5 h-3.5 text-[#00F0B5]" />
              </span>
              <span>CCTV Cameras ({cameras.length} active GPS · Click to stream)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 border border-dashed border-[#FF4FD8] shrink-0" />
              <span>Vijayawada planning perimeter</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 rounded shrink-0" style={{ background: "linear-gradient(90deg, #FF8A3D, #F5D547, #F472B6, #FB7185, #A78BFA, #34D399)" }} />
              <span>City entry routes · individually coloured</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 rounded shrink-0 bg-[#C084FC]" />
              <span>Railway station → temple</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 rounded shrink-0 bg-[#38BDF8]" />
              <span>Bus stand → temple</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 rounded shrink-0 bg-[#E9C46A]" />
              <span>Holding-area devotee path</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 shrink-0 bg-white" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #EAF4FB, #EAF4FB 2px, transparent 2px, transparent 4px)' }} />
              <span>Pedestrian footbridge</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 rounded shrink-0 bg-[#FF4F42]" />
              <span>Critical traffic incident</span>
            </div>

            <h3 className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 pt-1.5 mt-1 border-t border-neutral-200 dark:border-[#222838]">
              Temple inflow &amp; outflow density
            </h3>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 rounded shrink-0 bg-[#22C55E]" />
              <span>Temple inflow density</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 rounded shrink-0 bg-[#06B6D4]" />
              <span>Temple outflow density</span>
            </div>
            <div className="h-1.5 rounded-sm bg-gradient-to-r from-[#00F0B5] via-[#FFD43B] to-[#FF384C]" />
            <div className="flex justify-between text-[9.5px] text-neutral-400 font-semibold px-0.5">
              <span>free</span>
              <span>tight</span>
              <span>dangerous</span>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <span className="w-4 h-1.5 rounded shrink-0 bg-neutral-800 dark:bg-neutral-900 border border-neutral-700 flex items-center justify-around px-0.5">
                <span className="w-1 h-1 rounded-full bg-white animate-pulse" />
                <span className="w-1 h-1 rounded-full bg-white animate-pulse" />
              </span>
              <span>Direction of travel (flowing dots)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 rounded shrink-0 bg-[#8C7BF5]" />
              <span>Protocol / VIP route</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-1 rounded shrink-0 bg-[#3A4E64]" />
              <span>Road closed by control room</span>
            </div>
          </div>
        )}
      </div>

      {/* 7. LIVE CCTV CAMERA STREAM MODAL */}
      {activeModalCheckpoint && (
        <CameraStreamModal
          checkpoint={activeModalCheckpoint}
          initialCameraId={activeModalCameraId}
          onClose={() => {
            setActiveModalCheckpoint(null);
            setActiveModalCameraId(undefined);
          }}
        />
      )}
    </div>
  );
};

export default OperationsPage;
